'use client';

import { Button } from '~/components/ui/button';
import { Form } from '../ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '~/trpc/client';
import { FormInput } from '../forms/form-input';
import { FormTextArea } from '../forms/form-textarea';
import { FormSelect } from '../forms/form-select';
import {
  type LeadGenerationFormSchema,
  leadGenerationFormSchema,
} from '~/validation-schemas/lead-generation-form';
import { toast } from 'sonner';
import { isTRPCClientError } from '~/trpc/is-trpc-error';
import { cn } from '~/lib/utils';
import { type CallToActionButtonStyleReturn } from './call-to-action';
import { Checkbox } from '../ui/checkbox';
import { useMemo } from 'react';

export type BlockLeadGenerationFormProps = {
  className?: string;
  customButton: CallToActionButtonStyleReturn;
  formIdentifier: string;
  pageUrl: string;
  isWebFmsForm: boolean;
};

export function BlockLeadGenerationForm({
  className,
  customButton,
  formIdentifier,
  pageUrl,
  isWebFmsForm,
}: Readonly<BlockLeadGenerationFormProps>) {
  const submitLeadGenerationForm =
    trpc.formRouter.submitLeadGenerationForm.useMutation();

  const { data: fmsOptionsData, isLoading: isFmsOptionsLoading } =
    trpc.formRouter.getFmsOptions.useQuery(undefined, {
      enabled: isWebFmsForm,
    });

  const webFmsOptions = useMemo(() => {
    if (!fmsOptionsData?.fmsList) return [];

    const options = fmsOptionsData.fmsList.map((fms) => ({
      value: fms.name || '',
      label: fms.name || '',
    }));

    return options;
  }, [fmsOptionsData]);

  const form = useForm<LeadGenerationFormSchema>({
    resolver: zodResolver(leadGenerationFormSchema),
    defaultValues: {
      fullName: '',
      phone: '',
      email: '',
      establishmentAddress: '',
      establishmentName: '',
      notes: '',
      marketingSignup: false,
      webFMSChoice: '',
      otherWebFMSSystem: '',
      isWebFmsForm: isWebFmsForm,
    },
  });

  const watchedWebFMSChoice = form.watch('webFMSChoice');

  const onSubmit = async (data: LeadGenerationFormSchema) => {
    if (submitLeadGenerationForm.isPending) return;
    try {
      await toast
        .promise(
          submitLeadGenerationForm.mutateAsync({
            ...data,
            formIdentifier,
            pageUrl,
            isWebFmsForm,
          }),
          {
            loading: 'Submitting lead generation form...',
            success: () => {
              return `Lead generation form submitted successfully`;
            },
            error: (error) => {
              if (isTRPCClientError(error)) {
                return error.message;
              }
              return `Error submitting lead generation form`;
            },
          },
        )
        .unwrap();
      form.reset();
    } catch (error) {
      console.error('Error submitting lead generation form:', error);
    }
  };

  const isDisabled = form.formState.isSubmitting;

  return (
    <div className={cn('w-full sm:mx-auto sm:max-w-3xl', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2">
            <div className="col-span-1">
              <FormInput
                id="fullName"
                name="fullName"
                label="Full Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your full name"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="phone"
                name="phone"
                label="Phone Number"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="email"
                name="email"
                label="Email Address"
                type="email"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your email address"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="establishmentName"
                name="establishmentName"
                label="Establishment Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter establishment name"
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <FormInput
                id="establishmentAddress"
                name="establishmentAddress"
                label="Establishment Address"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter establishment address"
              />
            </div>
            {isWebFmsForm && (
              <>
                <div className="col-span-1">
                  <FormSelect
                    key={String(form.watch('webFMSChoice'))}
                    id="webFMSChoice"
                    name="webFMSChoice"
                    label="Web FMS System"
                    control={form.control}
                    disabled={isDisabled || isFmsOptionsLoading}
                    placeholder={
                      isFmsOptionsLoading
                        ? 'Loading options...'
                        : 'Select your FMS system'
                    }
                    options={webFmsOptions}
                  />
                </div>
                {watchedWebFMSChoice === 'Other' && (
                  <div className="col-span-1">
                    <FormInput
                      id="otherWebFMSSystem"
                      name="otherWebFMSSystem"
                      label="Other FMS System"
                      control={form.control}
                      disabled={isDisabled}
                      placeholder="Please specify"
                    />
                  </div>
                )}
                <div className="col-span-1 sm:col-span-2">
                  <FormTextArea
                    id="notes"
                    name="notes"
                    label="Notes (Optional)"
                    control={form.control}
                    disabled={isDisabled}
                    placeholder="Enter any additional notes"
                    rows={3}
                  />
                </div>
              </>
            )}
            <div className="col-span-1 sm:col-span-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="marketingSignup"
                  checked={form.watch('marketingSignup')}
                  onCheckedChange={(checked) => {
                    form.setValue('marketingSignup', checked === true);
                  }}
                  disabled={isDisabled}
                />
                <label
                  htmlFor="marketingSignup"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Sign up for marketing communications
                </label>
              </div>
            </div>
            <div className="col-span-1 mt-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={isDisabled}
                style={customButton?.customButtonStyle}
                className={cn(
                  'mx-auto block w-full sm:w-1/2',
                  customButton?.customButtonClasses,
                )}
                variant={customButton ? 'custom' : 'default'}
              >
                Submit Lead Generation Form
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
